import React from 'react';
import PropTypes from 'prop-types';
import { groupBy, orderBy, uniqBy } from 'lodash';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import GridList from '@material-ui/core/GridList';
import GridListTile from '@material-ui/core/GridListTile';
import useCols from '../../utils/useCols';
import useWidth from '../../utils/useWidth';
import { getLabel, getPlainTextLabel } from './utils';

import Section from '../UiSection';
import Card from './Card';

const CAPPED = 9;

const sortGroups = (groups, labels) => {
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const compA = getPlainTextLabel(a, labels);
    const compB = getPlainTextLabel(b, labels);
    if (groups[a].length <= 3 || groups[b].length <= 3) return 0;
    return compA.localeCompare(compB);
  });
  return sortedKeys.map(key => [key, groups[key]]);
};

const useStyles = makeStyles(theme => ({
  wrap: {
    ...theme.mixins.maxWidth(),
    padding: theme.spacing(1),
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(12),
  },
  groupTitle: {
    alignSelf: 'center',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    // textTransform: 'capitalize',
  },
  showAllButton: {
    marginTop: theme.spacing(4),
    alignSelf: 'center',
  },
  list: {
    overflow: 'visible',
  },
  tile: {
    overflow: 'visible',
  },
}));

const Group = props => {
  const classes = useStyles(props);
  const cols = useCols();
  const width = useWidth();
  const [showAll, setShowAll] = React.useState(false);
  const toggleShowAll = () => {
    setShowAll(!showAll);
  };

  const renderData = showAll ? props.data : props.data.slice(0, CAPPED);
  const buttonLabel = showAll ? (
    'Weniger Anzeigen'
  ) : (
    <React.Fragment>
      Alle Anzeigen (+{props.data.length - CAPPED})
    </React.Fragment>
  );
  const showButton = props.data.length > CAPPED;
  return (
    <div className={classes.group}>
      {props.groupTitle && (
        <Typography
          className={classes.groupTitle}
          variant={width === 'xs' ? 'subtitle2' : 'h5'}
          gutterBottom
        >
          <b>{props.groupTitle}</b>
        </Typography>
      )}
      <GridList
        className={classes.list}
        cellHeight="auto"
        cols={cols}
        spacing={16}
      >
        {renderData.map(p => (
          <GridListTile classes={{ tile: classes.tile }} key={p.uuid} cols={1}>
            <Card data={p} context={props.context} />
          </GridListTile>
        ))}
      </GridList>
      {showButton && (
        <Button
          className={classes.showAllButton}
          variant={showAll ? 'contained' : 'outlined'}
          onClick={toggleShowAll}
        >
          {buttonLabel}
        </Button>
      )}
    </div>
  );
};

const createGroupedData = (props, data) => {
  const groupedData = groupBy(data, props.groupBy);
  const withoutDuplicatesInGroups = Object.keys(groupedData).reduce(
    (acc, groupName) => {
      acc[groupName] = uniqBy(groupedData[groupName], 'uuid');
      return acc;
    },
    {},
  );
  return sortGroups(withoutDuplicatesInGroups, props.groupLabels);
};

const Results = props => {
  const { groupBy, groupLabels, context, data } = props;
  const sortedData = orderBy(data, 'status');
  const theme = useTheme();
  const classes = useStyles(props);
  const groupedData = groupBy
    ? createGroupedData(props, sortedData)
    : sortedData;
  const isEmpty = sortedData.length === 0;
  return (
    <Section bgColor={theme.palette.sketch.grey3}>
      <div className={classes.wrap}>
        {!isEmpty &&
          (groupBy ? (
            groupedData.map(([groupTitle, data]) => (
              <Group
                key={groupTitle}
                data={data}
                groupTitle={getLabel(groupTitle, groupLabels)}
                context={context}
              />
            ))
          ) : (
            <Group data={sortedData} context={context} />
          ))}
        {isEmpty && (
          <Typography align="center" display="block" variant="caption">
            Keine Ergebnisse gefunden
          </Typography>
        )}
      </div>
    </Section>
  );
};

export default Results;
